import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  TENANT_ALERT_SERVICE_CRIMINAL_SEARCH,
  TENANT_ALERT_SERVICE_EVICTION_SEARCH,
  TENANT_ALERT_SERVICE_SEX_OFFENDER_SEARCH,
  TENANT_ALERT_SERVICE_TERRORIST_SEARCH,
} from 'common/tenantAlertConstants';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
} from 'common/applicationAssemblyLineConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE,
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_ORANGE,
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_RED,
} from 'client/components/SrFormikField';
import {
  SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED,
} from 'client/components/SrCollapsibleContainer/SrCollapsibleContainer';
import SrTenantAlertApplicationViewer from 'client/components/SrTenantAlertApplicationViewer';
import SrApplicationAssemblyLineContainer from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineContainer';
import SrApplicationAssemblyLineContainerBox from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineContainerBox';

const getFormFields = ({
  formFieldId,
  onChange,
}) => {
  return [{
    id: formFieldId,
    title: 'Based on these records, is the Applicant still qualified?',
    titleSecondary: '(If you need help verifying this applicant ask your manager for help.)',
    type: 'yes_no',
    validations: ['required'],
    properties: {
      onChange: onChange,
    },
  }];
};

const SrBackgroundCheckRecordsFound = ({
  title,
  formFieldId,
  user,
  tenantAlertReport,
  tenantAlertServiceId,
  status,
  assemblyLineApplication,
  updateAssemblyLineApplication,
}) => {
  const formFields = getFormFields({
    formFieldId,
    onChange: (newValue) => {
      updateAssemblyLineApplication({
        assemblyLineApplicationId: assemblyLineApplication.id,
        updates: {
          [formFieldId]: newValue,
        },
      });
    },
  });

  let theme = SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE;
  if (status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS) {
    theme = SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_ORANGE;
  }
  else if (status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED) {
    theme = SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE_RED;
  }

  const descriptionDOM = tenantAlertServiceId ? (
    <SrTenantAlertApplicationViewer
      user={user}
      tenantAlertReport={tenantAlertReport}
      tenantAlertServiceId={tenantAlertServiceId}
    >
      <SrText color='blue300' weight='700'>
        {title}
      </SrText>
    </SrTenantAlertApplicationViewer>
  ) : (
    <SrText>
      {title}
    </SrText>
  );

  return (
    <SrBox>
      <SrApplicationAssemblyLineContainerBox>
        <SrText color='orange300' weight='700'>
          Records Found:
        </SrText>
        <br />
        {descriptionDOM}
      </SrApplicationAssemblyLineContainerBox>
      <SrBox marginTop='20px'>
        <Formik
          initialValues={assemblyLineApplication || {}}
          render={(formikBag) => (
            <Form className='form'>
              <SrFormikField
                formField={formFields[0]}
                formikBag={formikBag}
                theme={theme}
              />
            </Form>
          )}
        />
      </SrBox>
    </SrBox>
  );
};

SrBackgroundCheckRecordsFound.defaultProps = {
  title: 'Click to see the records',
};

const SrBackgroundCheckNoRecordsFound = ({
  title,
}) => (
  <SrApplicationAssemblyLineContainerBox>
    <SrText color='green' weight='700'>
      {title}
    </SrText>
  </SrApplicationAssemblyLineContainerBox>
);

SrBackgroundCheckNoRecordsFound.defaultProps = {
  title: 'No records were found',
};

const getItems = ({
  assemblyLine,
  householdApplication,
  updateAssemblyLineApplication,
}) => {
  const {
    assemblyLineApplication,
  } = householdApplication;
  const {
    tenantAlertReports,
  } = assemblyLine;
  const {
    backgroundCheckWarningOk,
    backgroundCheckCriminalSearchOk,
    backgroundCheckEvictionSearchOk,
    backgroundCheckSexOffenderSearchOk,
    backgroundCheckFederalSearchOk,
  } = assemblyLineApplication || {};

  const items = [];

  const tenantAlertReport = tenantAlertReports.find(({ userId }) => userId === householdApplication.userId);
  const user = assemblyLine.renters.find((renter) => renter.id === householdApplication.userId);

  const warningItem = {
    title: 'Warnings',
  };
  if (!tenantAlertReport) {
    warningItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
    warningItem.component = () => null;
  }
  else if (!tenantAlertReport.warningsText) {
    warningItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    warningItem.component = () => (
      <SrBackgroundCheckNoRecordsFound title='No warnings' />
    );
  }
  else {
    warningItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
    if (backgroundCheckWarningOk === true) {
      warningItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    }
    else if (backgroundCheckWarningOk === false) {
      warningItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
    }
    warningItem.component = () => (
      <SrBackgroundCheckRecordsFound
        formFieldId='backgroundCheckWarningOk'
        status={warningItem.status}
        title={tenantAlertReport.warningsText}
        assemblyLineApplication={assemblyLineApplication}
        updateAssemblyLineApplication={updateAssemblyLineApplication}
      />
    );
  }
  items.push(warningItem);

  const nationalCriminalSearchItem = {
    title: 'Nationwide Criminal Search',
  };
  if (!tenantAlertReport) {
    nationalCriminalSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
    nationalCriminalSearchItem.component = () => null;
  }
  else if (!tenantAlertReport.hasFelonyRecord) {
    nationalCriminalSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    nationalCriminalSearchItem.component = SrBackgroundCheckNoRecordsFound;
  }
  else {
    nationalCriminalSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
    if (backgroundCheckCriminalSearchOk === true) {
      nationalCriminalSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    }
    else if (backgroundCheckCriminalSearchOk === false) {
      nationalCriminalSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
    }
    nationalCriminalSearchItem.component = () => (
      <SrBackgroundCheckRecordsFound
        user={user}
        tenantAlertReport={tenantAlertReport}
        tenantAlertServiceId={TENANT_ALERT_SERVICE_CRIMINAL_SEARCH}
        formFieldId='backgroundCheckCriminalSearchOk'
        status={nationalCriminalSearchItem.status}
        assemblyLineApplication={assemblyLineApplication}
        updateAssemblyLineApplication={updateAssemblyLineApplication}
      />
    );
  }
  items.push(nationalCriminalSearchItem);

  const nationalEvictionSearchItem = {
    title: 'Nationwide Eviction Search',
  };
  if (!tenantAlertReport) {
    nationalEvictionSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
    nationalEvictionSearchItem.component = () => null;
  }
  else if (!tenantAlertReport.hasEvictionRecord) {
    nationalEvictionSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    nationalEvictionSearchItem.component = SrBackgroundCheckNoRecordsFound;
  }
  else {
    nationalEvictionSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
    if (backgroundCheckEvictionSearchOk === true) {
      nationalEvictionSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    }
    else if (backgroundCheckEvictionSearchOk === false) {
      nationalEvictionSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
    }
    nationalEvictionSearchItem.component = () => (
      <SrBackgroundCheckRecordsFound
        user={user}
        tenantAlertReport={tenantAlertReport}
        tenantAlertServiceId={TENANT_ALERT_SERVICE_EVICTION_SEARCH}
        formFieldId='backgroundCheckEvictionSearchOk'
        status={nationalEvictionSearchItem.status}
        assemblyLineApplication={assemblyLineApplication}
        updateAssemblyLineApplication={updateAssemblyLineApplication}
      />
    );
  }
  items.push(nationalEvictionSearchItem);

  const sexOffenderSearchItem = {
    title: 'Sex Offender Search',
  };
  if (!tenantAlertReport) {
    sexOffenderSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
    sexOffenderSearchItem.component = () => null;
  }
  else if (!tenantAlertReport.hasOffenderRecord) {
    sexOffenderSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    sexOffenderSearchItem.component = SrBackgroundCheckNoRecordsFound;
  }
  else {
    sexOffenderSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
    if (backgroundCheckSexOffenderSearchOk === true) {
      sexOffenderSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    }
    else if (backgroundCheckSexOffenderSearchOk === false) {
      sexOffenderSearchItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
    }
    sexOffenderSearchItem.component = () => (
      <SrBackgroundCheckRecordsFound
        user={user}
        tenantAlertReport={tenantAlertReport}
        tenantAlertServiceId={TENANT_ALERT_SERVICE_SEX_OFFENDER_SEARCH}
        formFieldId='backgroundCheckSexOffenderSearchOk'
        status={sexOffenderSearchItem.status}
        assemblyLineApplication={assemblyLineApplication}
        updateAssemblyLineApplication={updateAssemblyLineApplication}
      />
    );
  }
  items.push(sexOffenderSearchItem);

  const federalSeachItem = {
    title: 'OFAC, Terrorist, & Federal Search',
  };
  if (!tenantAlertReport) {
    federalSeachItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
    federalSeachItem.component = () => null;
  }
  else if (!tenantAlertReport.hasFederalRecord) {
    federalSeachItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    federalSeachItem.component = SrBackgroundCheckNoRecordsFound;
  }
  else {
    federalSeachItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
    if (backgroundCheckFederalSearchOk === true) {
      federalSeachItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
    }
    else if (backgroundCheckFederalSearchOk === false) {
      federalSeachItem.status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
    }
    federalSeachItem.component = () => (
      <SrBackgroundCheckRecordsFound
        user={user}
        tenantAlertReport={tenantAlertReport}
        tenantAlertServiceId={TENANT_ALERT_SERVICE_TERRORIST_SEARCH}
        formFieldId='backgroundCheckFederalSearchOk'
        status={federalSeachItem.status}
        assemblyLineApplication={assemblyLineApplication}
        updateAssemblyLineApplication={updateAssemblyLineApplication}
      />
    );
  }
  items.push(federalSeachItem);

  return items;
};

const SrApplicationAssemblyLineStepBackgroundCheck = ({
  assemblyLine,
  householdApplication,
  updateAssemblyLineApplication,
}) => {
  const items = getItems({
    assemblyLine,
    householdApplication,
    updateAssemblyLineApplication,
  });

  return (
    <div>
      {items.map((item) => {
        const Component = item.component || (() => (<div>!!! </div>));
        return (
          <SrBox key={item.title} marginBottom='24px'>
            <SrApplicationAssemblyLineContainer
              title={item.title}
              status={item.status}
              borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED}
              isExpanded
            >
              <Component />
            </SrApplicationAssemblyLineContainer>
          </SrBox>
        );
      })}
    </div>
  );
};

SrApplicationAssemblyLineStepBackgroundCheck.propTypes = {
  assemblyLine: PropTypes.object.isRequired,
  householdApplication: PropTypes.object.isRequired,
  updateAssemblyLineApplication: PropTypes.func.isRequired,
};

SrApplicationAssemblyLineStepBackgroundCheck.stepTitle = 'Background Check';
SrApplicationAssemblyLineStepBackgroundCheck.displayName = 'SrApplicationAssemblyLineStepBackgroundCheck';

SrApplicationAssemblyLineStepBackgroundCheck.getStatus = ({
  assemblyLine,
  householdApplication,
}) => {
  const items = getItems({
    assemblyLine,
    householdApplication,
  });

  const statuses = items.map(({ status }) => status);

  if (statuses.includes(SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED)) {
    return SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
  }
  if (statuses.includes(SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS)) {
    return SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
  }
  if (statuses.includes(SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED)) {
    return SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
  }

  return SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
};

export default SrApplicationAssemblyLineStepBackgroundCheck;
